import { AnimatePresence, motion } from "motion/react";
import { useState, useEffect, useRef } from "react";
import { parseTone, extractDomain } from "../settings/BrandProfileCard";

// How long we wait for the site preview to load before falling back to a
// neutral placeholder panel.
const PREVIEW_LOAD_TIMEOUT_MS = 6000;

const SCAN_GRADIENT =
  "linear-gradient(to bottom, transparent, rgba(77,124,15,0.10), transparent)";

// ─── Row status indicator ─────────────────────────────────────────────────────

const StatusIcon = ({ status }) => {
  if (status === "done") {
    return (
      <span className="w-4 h-4 rounded-full bg-primary text-white flex items-center justify-center shrink-0 mt-0.5">
        <svg viewBox="0 0 12 12" className="w-2 h-2" fill="none">
          <path
            d="M2.5 6.2 4.8 8.5 9.5 3.8"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </span>
    );
  }

  if (status === "run") {
    return (
      <span className="w-4 h-4 rounded-full border-2 border-gray-200 border-t-primary animate-spin shrink-0 mt-0.5" />
    );
  }

  return (
    <span className="w-4 h-4 rounded-full border-[1.5px] border-gray-200 shrink-0 mt-0.5" />
  );
};

// ─── Skeleton primitives ──────────────────────────────────────────────────────

const Bar = ({ className = "" }) => (
  <div className={`bg-gray-100 rounded ${className}`} />
);

// ─── Row renderers, keyed by the loading-state key ────────────────────────────

const ROWS = {
  companyName: {
    label: "Name",
    minHeight: "min-h-[62px]",
    placeholder: () => <Bar className="h-2.5 w-3/5" />,
    content: (info) => (
      <span className="font-semibold text-gray-900">{info.companyName}</span>
    ),
  },
  companyDescription: {
    label: "Overview",
    minHeight: "min-h-[96px]",
    placeholder: () => (
      <div className="space-y-[7px]">
        <Bar className="h-2.5 w-full" />
        <Bar className="h-2.5 w-[85%]" />
        <Bar className="h-2.5 w-[55%]" />
      </div>
    ),
    content: (info) => (
      <p className="text-[13px] leading-relaxed text-gray-700 line-clamp-3">
        {info.companyDescription}
      </p>
    ),
  },
  companyLogo: {
    label: "Logo",
    minHeight: "min-h-[62px]",
    placeholder: () => <Bar className="w-[34px] h-[34px] rounded-lg" />,
    content: (info, url) => (
      <div className="flex items-center gap-2.5">
        {info.companyLogo ? (
          <img
            src={info.companyLogo}
            alt={info.companyName || "Logo"}
            className="w-[34px] h-[34px] rounded-lg object-contain bg-gray-50"
          />
        ) : (
          <div className="w-[34px] h-[34px] rounded-lg bg-primary/30 flex items-center justify-center text-[17px] font-bold text-secondary">
            {info.companyName?.charAt(0)?.toUpperCase()}
          </div>
        )}
        <span className="min-w-0 font-mono text-[11px] text-gray-400 truncate">
          {extractDomain(url)}
        </span>
      </div>
    ),
  },
  companyColors: {
    label: "Colors",
    minHeight: "min-h-[62px]",
    placeholder: () => (
      <div className="flex gap-2">
        <Bar className="w-[26px] h-[26px] rounded-full" />
        <Bar className="w-[26px] h-[26px] rounded-full" />
        <Bar className="w-[26px] h-[26px] rounded-full" />
      </div>
    ),
    content: (info) => {
      const colors = (info.companyColors || []).slice(0, 4);
      return (
        <div className="flex items-center gap-2">
          {colors.map((color, idx) => (
            <motion.span
              key={`${color}-${idx}`}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3, delay: idx * 0.06 }}
              className="w-[26px] h-[26px] rounded-full shrink-0"
              style={{ backgroundColor: color, border: "1px solid #e5e7eb" }}
            />
          ))}
          {/* The hex list only fits alongside three swatches */}
          {colors.length <= 3 && (
            <span className="ml-1 min-w-0 font-mono text-[11px] text-gray-400 truncate">
              {colors.join(" · ")}
            </span>
          )}
        </div>
      );
    },
  },
  companyTone: {
    label: "Tone",
    minHeight: "min-h-[62px]",
    placeholder: () => (
      <div className="flex gap-1.5">
        <Bar className="w-16 h-[22px] rounded-full" />
        <Bar className="w-14 h-[22px] rounded-full" />
      </div>
    ),
    content: (info) => (
      <div className="flex flex-wrap items-center gap-1.5">
        {parseTone(info.companyTone).map((tag, idx) => (
          <motion.span
            key={tag}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.3, delay: idx * 0.08 }}
            className="px-2.5 py-1 rounded-full bg-gray-100 text-xs font-medium text-gray-600"
          >
            {tag}
          </motion.span>
        ))}
      </div>
    ),
  },
};

const ProfileRow = ({ row, status, revealed, businessInfo, url, isLast }) => (
  <div
    className={`flex items-start gap-3 px-[18px] py-[15px] ${row.minHeight} ${
      isLast ? "" : "border-b border-gray-100"
    }`}
  >
    <StatusIcon status={status} />
    <span className="w-[92px] shrink-0 pt-0.5 text-[11px] font-medium uppercase tracking-[0.06em] text-gray-400">
      {row.label}
    </span>
    {/* The row's min-height keeps the swap from shifting the card */}
    <div className="flex-1 min-w-0 min-h-[20px] text-[13.5px] leading-[1.45]">
      {revealed ? (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          {row.content(businessInfo, url)}
        </motion.div>
      ) : (
        row.placeholder()
      )}
    </div>
  </div>
);

// ─── Site preview (left panel) ────────────────────────────────────────────────

const SitePreview = ({ url, previewImage, scanning }) => {
  // There is no reliable way to tell a blocked frame from a slow one, so the
  // fallback sits *behind* the frame: a site that renders paints over it, and a
  // site that never paints leaves it showing. The timeout only decides when we
  // add the explanatory line.
  const [timedOut, setTimedOut] = useState(false);

  useEffect(() => {
    if (previewImage) return undefined;
    const timer = setTimeout(() => setTimedOut(true), PREVIEW_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [previewImage]);

  return (
    <div className="hidden md:flex flex-1 flex-col rounded-xl border border-gray-200 bg-white overflow-hidden">
      {/* Browser chrome */}
      <div className="h-[38px] shrink-0 flex items-center gap-2 px-3.5 bg-gray-50 border-b border-gray-200">
        <span className="w-2.5 h-2.5 rounded-full bg-gray-200" />
        <span className="w-2.5 h-2.5 rounded-full bg-gray-200" />
        <span className="w-2.5 h-2.5 rounded-full bg-gray-200" />
        <div className="ml-2.5 flex-1 h-[22px] rounded-md bg-white border border-gray-200 flex items-center px-2.5 font-mono text-[10.5px] text-gray-500 truncate">
          {url}
        </div>
      </div>

      {/* Page */}
      <div className="relative flex-1 overflow-hidden bg-white">
        {/* Fallback, painted over by the site itself when it renders */}
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-8 text-center">
          <svg
            className="w-8 h-8 text-gray-300"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Zm0 0c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3 7.5 7.03 7.5 12s2.015 9 4.5 9ZM3.6 9h16.8M3.6 15h16.8"
            />
          </svg>
          <p className="font-mono text-[11px] text-gray-400">
            {extractDomain(url)}
          </p>
          {timedOut && (
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="text-xs text-gray-400 max-w-[280px]"
            >
              This site can't be previewed here, but we're still reading it.
            </motion.p>
          )}
        </div>

        {previewImage ? (
          <img
            src={previewImage}
            alt={`${extractDomain(url)} homepage`}
            className="relative z-10 w-full h-full object-cover object-top"
          />
        ) : (
          // NOTE: sites that send X-Frame-Options / frame-ancestors can't be
          // framed, and the browser fires `load` for its own error page either
          // way — Resource Timing reports the same opaque values for a frame
          // that painted and one that didn't. So there is nothing to detect:
          // the frame is transparent, and the fallback above shows through
          // whenever it renders nothing.
          //
          // `sandbox=""` is the most restrictive value (no scripts, opaque
          // origin, no forms or navigation). Third-party JS was crashing the
          // subframe into Chrome's opaque grey error page, which hid the
          // fallback; without it server-rendered pages still paint their HTML,
          // CSS and images. Client-rendered SPAs now show the fallback instead.
          //
          // A server-side screenshot passed in as `previewImage` is the fix
          // that works for every site.
          url && (
            <iframe
              src={url}
              title={`${extractDomain(url)} homepage`}
              sandbox=""
              referrerPolicy="no-referrer"
              loading="lazy"
              className="relative z-10 w-full h-full border-0 block bg-transparent pointer-events-none"
            />
          )
        )}

        {/* Scan line */}
        {scanning && (
          <div
            className="absolute inset-x-0 h-[16%] z-20 pointer-events-none animate-site-scan"
            style={{
              background: SCAN_GRADIENT,
              borderBottom: "1.5px solid rgba(77,124,15,0.55)",
            }}
          />
        )}
      </div>
    </div>
  );
};

// ─── Main Loader ──────────────────────────────────────────────────────────────

export const MultiStepLoaderBusinessDetails = ({
  loadingStates,
  loading,
  setLoading,
  duration = 2000,
  loop = false,
  businessInfo = null,
  url = null,
  previewImage = null,
}) => {
  const [currentState, setCurrentState] = useState(0);
  const [finished, setFinished] = useState(false);
  const finishTimerRef = useRef(null);

  // Only the states we know how to render land in the profile card; each keeps
  // its index in loadingStates so it stays in step with the progression.
  const rows = loadingStates
    .map((state, index) => ({ ...state, index }))
    .filter((state) => ROWS[state.key]);

  // Progression logic (identical to MultiStepLoader)
  useEffect(() => {
    if (!loading) {
      setCurrentState(0);
      setFinished(false);
      setLoading(false);
      return;
    }

    if (businessInfo === null) {
      return;
    }

    const timeout = setTimeout(() => {
      setCurrentState((prevState) =>
        loop
          ? prevState === loadingStates.length - 1
            ? 0
            : prevState + 1
          : Math.min(prevState + 1, loadingStates.length - 1),
      );
    }, duration);

    if (currentState === loadingStates.length - 1) {
      setFinished(true);
      finishTimerRef.current = setTimeout(() => {
        setLoading(false);
      }, 1000);
    }

    return () => clearTimeout(timeout);
  }, [
    currentState,
    loading,
    loop,
    loadingStates.length,
    duration,
    businessInfo,
    setLoading,
  ]);

  useEffect(() => () => clearTimeout(finishTimerRef.current), []);

  const statusFor = (index) => {
    if (finished) return "done";
    if (index < currentState) return "done";
    return index === currentState ? "run" : "wait";
  };

  return (
    <AnimatePresence mode="wait">
      {loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full h-full fixed inset-0 z-[101] bg-white flex items-center justify-center p-6 lg:p-10"
        >
          <div className="w-full max-w-[1280px] h-full max-h-[760px] flex gap-6 lg:gap-8">
            <SitePreview
              url={url}
              previewImage={previewImage}
              scanning={!finished}
            />

            {/* Brand profile */}
            <div className="w-full md:w-[448px] shrink-0 flex flex-col rounded-xl border border-gray-200 bg-white overflow-hidden">
              <div className="px-[18px] py-3.5 shrink-0 border-b border-gray-100 flex items-center justify-between">
                <span className="text-[13.5px] font-semibold text-gray-900">
                  Brand profile
                </span>
                <span className="px-2.5 py-[3px] rounded-full bg-[#f0f7e6] text-[11px] font-semibold text-primary">
                  {finished ? "Auto-detected" : "Detecting…"}
                </span>
              </div>

              <div className="flex-1 overflow-y-auto">
                {rows.map((state, position) => (
                  <ProfileRow
                    key={state.key}
                    row={ROWS[state.key]}
                    status={statusFor(state.index)}
                    revealed={
                      state.index <= currentState && !!businessInfo?.[state.key]
                    }
                    businessInfo={businessInfo || {}}
                    url={url}
                    isLast={position === rows.length - 1}
                  />
                ))}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
